import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { accumulation, scoreDay, scoreTrail } from '../../packages/domain/src/index'
import { loadSharedProjections, type ProjectedPerson } from '../features/sharing/api'
import { formatPoints } from '../features/display/preferences'
import { useDisplayPreferences } from '../features/display/useDisplayPreferences'
import { AccumulationDisplay } from '../components/AccumulationDisplay'
import { projectionAtCap } from '../features/sharing/contracts'
import { sharedPeople } from '../features/sharing/synthetic'
import { useOwnerMatrix } from '../owner/OwnerMatrixContext'
import { ScreenHeading } from './TodayScreen'
import { settingsHref } from './settingsRouting'

type PersonView = ProjectedPerson & { sync: string; since: string }

export function ScoreboardScreen() {
  const location = useLocation()
  const owner = useOwnerMatrix()
  const [axis, setAxis] = useState<'category' | 'person'>('category')
  const displayPreferences = useDisplayPreferences()
  const [people, setPeople] = useState<PersonView[]>(() => sharedPeople.map((person) => ({
    projection: projectionAtCap(person), stale: person.stale, sync: person.sync, since: person.since,
  })))
  const [fixtureSharing, setFixtureSharing] = useState(true)

  useEffect(() => {
    let active = true
    void loadSharedProjections().then((value) => {
      if (!active) return
      setPeople(value.people.map((person) => ({
        ...person,
        sync: person.projection.freshness ? new Date(person.projection.freshness).toLocaleString() : 'No recent sync',
        since: 'first recorded date',
      })))
      setFixtureSharing(false)
    }).catch(() => { /* Explicit synthetic fallback for an unconfigured local build. */ })
    return () => { active = false }
  }, [])

  const categoryScores = useMemo(() => owner.document.categories.map((category) => {
    const today = scoreDay(owner.document, owner.trustedToday, { categoryId: category.id })
    return {
      category,
      today,
      trail7: scoreTrail(owner.document, { id: '7d', label: '7 day', days: 7 }, owner.trustedToday, { categoryId: category.id }).score,
      trail28: scoreTrail(owner.document, { id: '28d', label: '28 day', days: 28 }, owner.trustedToday, { categoryId: category.id }).score,
    }
  }), [owner.document, owner.trustedToday])
  const ownerAccumulation = accumulation(owner.document, owner.trustedToday, owner.accumulationAnchor ?? undefined)
  const accumulationMax = Math.max(1, ...people.map((person) => person.projection.accumulation ?? 0))

  return (
    <div className="screen scoreboard-screen">
      <ScreenHeading eyebrow="Private workspace" title="Scoreboard" description={axis === 'category' ? 'Your earned score and shortfall, grouped by category.' : 'Shared projections show only what people earned—never their deficit.'}>
        <div className="history-actions"><Link className="button" to={settingsHref('scoreboard-display', location.search)}>Configure Scoreboard</Link><div className="segmented" aria-label="Group scoreboard">
          <button type="button" aria-pressed={axis === 'category'} onClick={() => setAxis('category')}>By category</button>
          <button type="button" aria-pressed={axis === 'person'} onClick={() => setAxis('person')}>By person</button>
        </div></div>
      </ScreenHeading>
      {axis === 'category' ? (
        <div className="score-grid">
          {categoryScores.map(({ category, today, trail7, trail28 }) => (
            <article className="score-tile panel" key={category.id}>
              <header><span className="overline">Category</span><h2>{category.name}</h2></header>
              <div className="tile-score">{formatPoints(today.score)}</div>
              {today.score === null ? <p className="no-score">No score today.</p> : <progress className="progress-track" value={today.score} max="100" aria-label={`${category.name} score ${Math.round(today.score)} out of 100`} />}
              <div className="tile-metrics"><span><strong>{formatPoints(today.score)}</strong><small>Today</small></span><span><strong>{formatPoints(trail7)}</strong><small>7-day</small></span><span><strong>{formatPoints(trail28)}</strong><small>28-day</small></span></div>
              <p>{today.score === null ? 'Nothing due today.' : `${today.remainingOperationIds.length} due operations remaining.`}</p>
            </article>
          ))}
          <article className="score-tile panel owner-total"><header><span className="overline">Owner accumulation</span><h2>All categories</h2></header><div className="tile-score">{formatPoints(ownerAccumulation.total)}</div><p>Banked since {ownerAccumulation.anchor ?? '—'}.</p></article>
        </div>
      ) : (
        <><p className="board-doctrine">Your board. Each person shows only the level they granted you. It never shows deficits. {fixtureSharing && <strong>Synthetic sharing preview—backend sharing is not configured.</strong>}</p><div className="score-grid person-grid">
          {people.map(({ projection, stale, sync, since }, index) => {
            const trail7 = projection.trails[0]
            const trail28 = projection.trails[1]
            return <article className={`score-tile person-tile panel ${stale ? 'stale' : ''}`} key={`${projection.displayName}-${index}`}>
              <header><span className="level-chip">{projection.level} shared</span><h2>{projection.displayName}</h2></header>
              {stale ? <p className="stale-message">Score unavailable · {sync}</p> : <>{projection.score === null ? <p className="tile-score">—</p> : <><div className="tile-score">{formatPoints(projection.score)}</div><progress className="earned-bar" value={projection.score} max="100" aria-label={`${projection.displayName} earned score ${formatPoints(projection.score)}`} /></>}</>}
              {!stale && <div className="tile-metrics"><span><strong>{formatPoints(trail7)}</strong><small>7-day</small></span><span><strong>{formatPoints(trail28)}</strong><small>28-day</small></span></div>}
              {displayPreferences.scoreboardCategories && projection.categories && <div className="shared-categories">{projection.categories.map((category) => <span key={category.name}>{category.name}<strong>{formatPoints(category.earnedScore)}</strong></span>)}</div>}
              {displayPreferences.scoreboardSuccessfulItems && projection.successfulItems && <div className="earned-items">{projection.successfulItems.map((item) => <span key={item}>{item}</span>)}</div>}
              {!stale && <AccumulationDisplay mode={displayPreferences.scoreboardAccumulation} total={projection.accumulation} maximum={accumulationMax} name={projection.displayName} since={since} freshness={sync} singlePerson={people.length === 1} />}
            </article>
          })}
        </div></>
      )}
    </div>
  )
}
